import { useState, useEffect, useEffectEvent, useLayoutEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Bell, Zap, AlertTriangle, Info, CheckCircle2, Hand } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { notificationDb } from '@/lib/notificationDb'
import { uniqueTopic } from '@/lib/realtime'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { claimOutcomeMessage, offerIdOfNotification, offerLeadName, offerStatusLine, type LeadOffer } from '@/lib/leadOffers'
import { useClaimLeadOffer, useMyLeadOffers } from '@/hooks/useLeadOffers'

interface Notification {
  id: string; title: string; body: string | null; type: string;
  link: string | null; read_at: string | null; created_at: string;
  // mig 295 (RIZ020): false when the recipient switched the in-app channel off
  // for this kind of event. The row still exists — it may have gone out by
  // email, and it is the record that the event happened — but the bell skips
  // it. Nullable in the type because rows written before mig 295 predate the
  // column in any cached response shape; the column itself is NOT NULL.
  in_app?: boolean | null
  // RIZ127 (mig 470): a First to Claim offer is event_type 'lead.offer' with data.offer_id; the row
  // then carries a Claim action, or says who got the lead.
  event_type?: string | null
  data?: unknown
}

// Module scope: reads the clock, so it must stay out of the component render
// path (react-hooks/purity).
function timeAgo(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (diff < 60) return 'now'
  if (diff < 3600) return Math.floor(diff / 60) + 'm'
  if (diff < 86400) return Math.floor(diff / 3600) + 'h'
  return Math.floor(diff / 86400) + 'd'
}

// Module scope for the same reason: it reads the clock.
function offerLineNow(offer: LeadOffer, meId: string | null): string {
  return offerStatusLine(offer, meId, Date.now())
}

// RIZ132: a tapped push alert opens its page with ?n=<the notification's id> on the address
// (supabase/functions/_shared/push-rules.ts withTapParam puts exactly one n there, and it is ours).
// The bell marks that row read on arrival, so the badge agrees with the phone, and drops the
// parameter. Anything in n that is not a notification id is left alone.
const TAPPED_ALERT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// TWO BELLS ARE MOUNTED, the sidebar's and the mobile header's (CSS shows one of them), so an
// arrival is claimed here, once, by the location it came on: the first bell to see it marks the
// row and drops the parameter, and every bell reads its rows again once that mark has settled, so
// the one on screen is right whichever one did the marking.
let tappedArrival: { locationKey: string; marked: Promise<void> } | null = null

export function NotificationBell() {
  const { user } = useAuth()
  // Key the realtime effect on the stable id string, not the user OBJECT — its
  // identity churns while auth settles (getSession + onAuthStateChange), which
  // would otherwise re-run the subscribe effect and (pre-uniqueTopic) crash.
  const userId = user?.id
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [open, setOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  // The bell sits inside the sidebar, whose `overflow-y-auto` also clips on the x-axis,
  // so an absolutely-positioned panel can't escape it (and `right-0` ran it off the left
  // edge). Anchor a viewport-fixed panel to the bell's rect instead, clamped on-screen —
  // works at the sidebar's left edge (desktop) and the header's right edge (mobile).
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // RIZ127: the offers behind this panel's "Lead to claim" rows, read live when the panel opens -
  // so a row says "Claimed by <name>" the moment someone else wins, not when a new row arrives.
  const offerIds = useMemo(
    () => [...new Set(notifications.map(offerIdOfNotification).filter((x): x is string => !!x))],
    [notifications],
  )
  const { offers } = useMyLeadOffers({ offerIds, enabled: open && offerIds.length > 0 })
  const { claim, busyId: claimingId } = useClaimLeadOffer()
  const [offerNotes, setOfferNotes] = useState<Record<string, string>>({})

  // The newest read is the one that stays: a read that started earlier and answers later never writes
  // over it (a page's first read can answer after the re-read that follows a tapped alert's mark, below).
  const reads = useRef(0)
  const fetch = useCallback(async () => {
    if (!userId) return
    const read = ++reads.current
    // The badge counts ALL unread rows — counting within the 20-row panel
    // fetch undercounted once unread items aged past the newest 20.
    //
    // BOTH halves filter in_app (mig 295): a person who switched a kind of
    // alert off in Settings must not see it here AND must not have it counted
    // in the badge, or the badge says 3 and the panel shows 1.
    const [{ data }, { count }] = await Promise.all([
      notificationDb
        .from('notifications').select('*')
        .eq('user_id', userId)
        .eq('in_app', true)
        .order('created_at', { ascending: false }).limit(20),
      notificationDb
        .from('notifications').select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('in_app', true)
        .is('read_at', null),
    ])
    if (read !== reads.current) return

    if (data) {
      setNotifications(data as Notification[])
      setUnreadCount(count ?? data.filter(n => !n.read_at).length)
    }
  }, [userId])

  // I approve bandaid: pre-existing fetch-effect; real fix is the TanStack Query migration.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { fetch() }, [fetch])

  useEffect(() => {
    if (!userId) return
    const channel = supabase
      .channel(uniqueTopic('notifications'))
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'notifications',
        filter: 'user_id=eq.' + userId,
      }, (payload) => {
        // postgres_changes takes ONE filter, and it is spent on user_id, so the
        // in_app check has to happen here. Without it a switched-off alert would
        // still pop into the panel live and only disappear on the next remount.
        const row = payload.new as Notification
        if (row.in_app === false) return
        setNotifications(prev => [row, ...prev].slice(0, 20))
        setUnreadCount(prev => prev + 1)
      })
      // Reads made OUTSIDE this panel (opening a texting conversation marks
      // its bell rows read) refetch the badge + rows — without this the count
      // stayed stale until a remount.
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'notifications',
        filter: 'user_id=eq.' + userId,
      }, () => { void fetch() })
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [userId, fetch])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Position the fixed panel under the bell, opening leftward, clamped into the viewport.
  // I approve bandaid: DOM measurement in useLayoutEffect (measure → setState
  // before paint) is the React-documented pattern for positioning.
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!open) { setPos(null); return }
    function place() {
      const t = triggerRef.current?.getBoundingClientRect()
      if (!t) return
      const margin = 8
      const width = Math.min(320, window.innerWidth - margin * 2)
      let left = t.right - width // align right edges → panel opens leftward from the bell
      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin))
      setPos({ top: t.bottom + margin, left, width })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  async function markRead(id: string) {
    // Snapshot the row's prior read state so a failed update doesn't leave the optimistic
    // badge permanently diverged from the DB (the update used to discard its { error }).
    const wasUnread = notifications.some(n => n.id === id && !n.read_at)
    const now = new Date().toISOString()
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: n.read_at ?? now } : n))
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1))
    const { error } = await supabase.from('notifications').update({ read_at: now }).eq('id', id)
    if (error) {
      console.error('NotificationBell: could not mark the notification read:', error.message)
      // Revert the optimistic change so the badge reflects the true unread count.
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: wasUnread ? null : n.read_at } : n))
      if (wasUnread) setUnreadCount(prev => prev + 1)
    }
  }

  // RIZ132: the ?n= arrival (see TAPPED_ALERT_ID). It waits for the person, because a mark made before
  // there is a session changes nothing (RLS), and it waits for nothing else: the parameter is dropped
  // in the same run that starts the mark, whatever the mark then does. markRead is the one the rows use;
  // it needs the newest list, so the effect reaches it through an effect event.
  const markTappedRead = useEffectEvent((id: string): Promise<void> => markRead(id))
  useEffect(() => {
    if (!userId) return
    const params = new URLSearchParams(location.search)
    const id = params.get('n')
    if (id === null || !TAPPED_ALERT_ID.test(id)) return
    let arrival = tappedArrival
    if (arrival?.locationKey !== location.key) {
      params.delete('n')
      const rest = params.toString()
      arrival = {
        locationKey: location.key,
        // Started a microtask later: markRead begins with the rows' optimistic update, a state change an
        // effect must not make in its own body (react-hooks/set-state-in-effect).
        marked: Promise.resolve()
          .then(() => markTappedRead(id))
          .catch((err: unknown) => {
            console.error('NotificationBell: could not mark the tapped alert read:', err)
          }),
      }
      tappedArrival = arrival
      // A replace, so Back never returns to it; the path, the other parameters and the fragment stay.
      navigate({ pathname: location.pathname, search: rest === '' ? '' : `?${rest}`, hash: location.hash }, { replace: true })
    }
    void arrival.marked.then(() => fetch())
  }, [userId, location.key, location.pathname, location.search, location.hash, navigate, fetch])

  async function claimFromBell(n: Notification, offer: LeadOffer) {
    const outcome = await claim(offer.offer_id)
    if (!outcome) {
      setOfferNotes(prev => ({ ...prev, [offer.offer_id]: 'Could not claim it. Try again from Leads to claim.' }))
      return
    }
    if (!n.read_at) void markRead(n.id)
    if (outcome.outcome === 'claimed') {
      setOpen(false)
      navigate(`/contacts/${outcome.contact_id}`)
      return
    }
    setOfferNotes(prev => ({ ...prev, [offer.offer_id]: claimOutcomeMessage(outcome) }))
  }

  async function markAllRead() {
    if (!user) return
    // Snapshot so a failed bulk update can restore the exact prior read/unread state.
    const prevNotifications = notifications
    const prevUnread = unreadCount
    const now = new Date().toISOString()
    setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || now })))
    setUnreadCount(0)
    // Scoped to what this panel actually SHOWS (mig 295). "Mark all read" is an
    // action on the list in front of you; silently clearing rows the person
    // switched off would decide, on their behalf, that they had read alerts
    // they were never shown.
    const { error } = await notificationDb.from('notifications')
      .update({ read_at: now })
      .eq('user_id', user.id)
      .eq('in_app', true)
      .is('read_at', null)
    if (error) {
      // Revert so the badge isn't left permanently showing zero while rows are still unread.
      setNotifications(prevNotifications)
      setUnreadCount(prevUnread)
    }
  }

  const icons: Record<string, React.ElementType> = { ai: Zap, alert: AlertTriangle, action: CheckCircle2, info: Info }

  const bellLabel = unreadCount > 0 ? 'Notifications, ' + unreadCount + ' unread' : 'Notifications'

  return (
    <div className="relative">
      <span role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {unreadCount > 0 ? unreadCount + ' unread notification' + (unreadCount === 1 ? '' : 's') : ''}
      </span>
      <button ref={triggerRef} onClick={() => setOpen(!open)}
        aria-label={bellLabel} aria-expanded={open} aria-haspopup="dialog"
        className="relative flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <Bell className="h-4 w-4" aria-hidden="true" />
        {/* Badge offsets track the centered glyph, not the (now 44px) button
            box, so the dot stays visually attached to the bell. */}
        {unreadCount > 0 && (
          <span aria-hidden="true" className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {open && pos && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); triggerRef.current?.focus() }} aria-hidden="true" />
          <div ref={panelRef} role="dialog" aria-modal="true" aria-label="Notifications" style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width }} className="z-50 rounded-lg border border-border bg-card shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold">Notifications</h2>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="rounded text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Mark all read</button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto" role="list" aria-label="Notification list">
              {notifications.length === 0 ? (
                <p className="p-4 text-center text-xs text-muted-foreground">No notifications</p>
              ) : (
                notifications.map(n => {
                  const Icon = icons[n.type] || Info
                  return (
                    // The button used to carry role="listitem" itself, which overrode
                    // its own button semantics with a non-interactive role — the row
                    // stopped being announced as something you can press. The listitem
                    // is the wrapper; the button stays a button inside it.
                    <div key={n.id} role="listitem">
                      <button
                        aria-label={n.title + (n.read_at ? '' : ', unread')}
                        onClick={() => { if (!n.read_at) markRead(n.id); if (n.link) window.location.href = n.link; setOpen(false) }}
                        className={cn('flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', !n.read_at && 'bg-primary/5')}
                      >
                        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', n.type === 'ai' ? 'text-primary' : 'text-muted-foreground')} aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium">{n.title}</p>
                          {n.body && <p className="mt-0.5 text-[10px] text-muted-foreground line-clamp-2">{n.body}</p>}
                          <p className="mt-0.5 text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</p>
                        </div>
                        {!n.read_at && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />}
                      </button>
                      {/* RIZ127: a First to Claim offer - claim it right here, or see who got it.
                          A sibling of the row button, never inside it (a button in a button is
                          invalid and unreachable by keyboard). */}
                      {(() => {
                        const offerId = offerIdOfNotification(n)
                        if (!offerId) return null
                        const offer = offers.find(o => o.offer_id === offerId)
                        if (!offer) return null
                        const note = offerNotes[offerId]
                        return (
                          <div data-testid="bell-offer" className="flex items-center gap-2 px-4 pb-3 pl-11">
                            <p className={cn('min-w-0 flex-1 text-[10px]', offer.can_claim ? 'font-medium text-primary' : 'text-muted-foreground')}>
                              {note ?? offerLineNow(offer, user?.id ?? null)}
                            </p>
                            {offer.can_claim && !note && (
                              <button
                                type="button"
                                onClick={() => void claimFromBell(n, offer)}
                                disabled={claimingId === offerId}
                                aria-label={`Claim ${offerLeadName(offer)}`}
                                className="flex min-h-[32px] items-center gap-1 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                <Hand className="h-3.5 w-3.5" aria-hidden="true" />
                                {claimingId === offerId ? 'Claiming…' : 'Claim'}
                              </button>
                            )}
                          </div>
                        )
                      })()}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

